import type { Contact, Survey, SurveyGroup } from "@/features/survey/types";
import { interactiveSurveyApi } from "./base";

// Survey Group API for multi-market cover page operations
export const surveyGroupApi = interactiveSurveyApi
  .enhanceEndpoints({
    addTagTypes: ["SurveyGroup"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getSurveyGroupDetails: build.query<SurveyGroup, string>({
        query: (surveyGroupId) => ({
          url: `/surveyGroup/${surveyGroupId}`,
        }),
        providesTags: (_result, _error, surveyGroupId) => [
          { type: "SurveyGroup", id: surveyGroupId },
        ],
      }),
      // Fetches the survey group, then resolves each enabled member
      // survey (by surveyId) into its full survey details.
      getSurveyGroupWithSurveys: build.query<
        { surveyGroup: SurveyGroup; surveys: Survey[] },
        string
      >({
        queryFn: async (surveyGroupId, _api, _extraOptions, fetchWithBQ) => {
          const groupResult = await fetchWithBQ(
            `/surveyGroup/${surveyGroupId}`,
          );
          if (groupResult.error) {
            return { error: groupResult.error };
          }

          const surveyGroup = groupResult.data as SurveyGroup;
          const memberSurveyIds = surveyGroup.surveys
            .filter((member) => member.isSurveyEnabled)
            .sort((a, b) => a.order - b.order)
            .map((member) => member.surveyId);

          const surveyResults = await Promise.all(
            memberSurveyIds.map((surveyId) =>
              fetchWithBQ(`/survey/${surveyId}`),
            ),
          );

          const failedResult = surveyResults.find((result) => result.error);
          if (failedResult?.error) {
            return { error: failedResult.error };
          }

          const surveys = surveyResults.map(
            (result) => result.data as Survey,
          );

          return { data: { surveyGroup, surveys } };
        },
        providesTags: (_result, _error, surveyGroupId) => [
          { type: "SurveyGroup", id: surveyGroupId },
        ],
      }),
      // Survey groups don't embed contacts in their own response, so they're
      // fetched separately via the shared contacts endpoint (isGroup=true).
      getSurveyGroupContacts: build.query<Contact[], string>({
        query: (surveyGroupId) => ({
          url: `/contacts/survey/${surveyGroupId}`,
          params: { isGroup: true },
        }),
        providesTags: (_result, _error, surveyGroupId) => [
          { type: "SurveyGroup", id: `${surveyGroupId}-contacts` },
        ],
      }),
    }),
  });

export const {
  useGetSurveyGroupDetailsQuery,
  useGetSurveyGroupWithSurveysQuery,
  useGetSurveyGroupContactsQuery,
} = surveyGroupApi;
